"use client";

import { Component, type ReactNode } from "react";
import posthog from "posthog-js";
import { isChunkLoadError, reloadForChunkError } from "@/lib/chunk-reload";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

// React 内部で発生したエラー (Minified React error #185 = Maximum update
// depth exceeded など) を捕捉して PostHog Error Tracking に送る。
// `capture_exceptions: true` は window.onerror / unhandledrejection しか
// 捕らえないため、React render 中の throw は Error Boundary が必要。
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // 新デプロイで古いチャンクが 404 になった場合は自動リロードで復旧する
    // （真っ暗の fallback で放置しない）。リロードループはガードで防ぐ。
    if (isChunkLoadError(error) && reloadForChunkError()) {
      return;
    }

    try {
      posthog.captureException(error, {
        source: "react-error-boundary",
        componentStack: info.componentStack ?? undefined,
        url: typeof window !== "undefined" ? window.location.href : undefined,
      });
    } catch {
      // PostHog 側で失敗しても UI 復旧を優先
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
